/**
 * The lead's shortlist — what this buyer currently wants — DERIVED, never stored.
 *
 * Two sources feed it and neither is ever copied into the other: `LeadItem` is
 * what staff captured at the meeting, `QuoteRequestItem` is what the buyer
 * ticked on a catalogue link. Deriving instead of synchronising is the whole
 * design: a stored union would need an owner per row, and every rule about who
 * may overwrite whom is a rule somebody gets wrong.
 *
 * Consequences worth knowing, because they are the reason this shape was chosen:
 *  - deleting a quote request removes its lines here, with no un-apply step;
 *  - a booth line's negotiated quantity can never be overwritten by a remote
 *    tick, which is the 2026-08-07 MOQ incident made structurally impossible;
 *  - "they asked for 50" is a field on the line, not an edit to the buyer's note.
 */
import { z } from 'zod';
import { MoneyAmount } from './money.js';
/** Which side of the conversation put this line on the list. */
export const ShortlistSource = z.enum(['MEETING', 'REQUEST']);
/** In the office's words. */
export const SHORTLIST_SOURCE_LABEL = {
    MEETING: 'booth',
    REQUEST: 'from catalogue'
};
export const ShortlistLine = z.object({
    productId: z.string().min(1),
    productName: z.string().min(1),
    quantity: z.number().int().min(1),
    /**
     * Best price known for this line: the booth-negotiated one, else the marked-up
     * figure the buyer was actually shown. NULL when they were shown none at all
     * (a catalogue sent at BAND or NONE) — never 0.00, which would invent one.
     */
    unitPrice: MoneyAmount.nullable(),
    /** The catalogue price at the time, for the "(list ₹X)" comparison. */
    listPriceSnapshot: MoneyAmount.nullable(),
    note: z.string().nullable(),
    source: ShortlistSource,
    /**
     * Set only on a MEETING line the buyer later re-asked about at a DIFFERENT
     * quantity. The negotiated quantity stays authoritative and this records what
     * they said, so staff decide rather than a catalogue default deciding for them.
     *
     * Cleared when their latest ask agrees with the negotiated figure — otherwise
     * a buyer who corrected themselves would still be shown as wanting the number
     * they moved away from.
     */
    askedQuantity: z.number().int().min(1).nullable(),
    /**
     * The buyer's own wording for a line staff had already captured. It cannot go
     * in `note`, which is the staff-captured one, and dropping it would hide a
     * customization request ("gold finish, engrave logo") from the only screen
     * anyone reads before quoting.
     */
    askedNote: z.string().nullable()
});
/**
 * One captured line as a shortlist line, before any buyer ask is folded in.
 *
 * Exported because two clients build a shortlist without a server round-trip —
 * an unsynced draft, and a Dexie mirror row stored before `shortlist` existed —
 * and both were re-implementing this mapping by hand. What a MEETING line looks
 * like is one fact; it gets one implementation.
 */
export function meetingLine(item) {
    return {
        productId: item.productId,
        productName: item.productNameSnapshot,
        quantity: item.quantity,
        unitPrice: item.quotedPrice,
        listPriceSnapshot: item.listPriceSnapshot ?? null,
        note: item.note ?? null,
        source: 'MEETING',
        askedQuantity: null,
        askedNote: null
    };
}
/**
 * Meeting lines first and in their captured order, then products only the buyer
 * named, in the order they first asked for them. Stable, so the list does not
 * reshuffle under someone mid-read.
 *
 * A DECLINED ask is dropped HERE rather than by the caller. Declining is how
 * staff say "we are not doing this", and it is the only way to take a
 * buyer-added line off this list short of deleting their request — a rule worth
 * exactly one implementation, in the pure function every caller already uses.
 */
export function deriveShortlist({ meeting, requests }) {
    const byProduct = new Map();
    for (const item of meeting) {
        byProduct.set(item.productId, meetingLine(item));
    }
    // Oldest ask first, so a later one overwrites it on the fields it owns.
    const ordered = requests.filter(request => request.status !== 'DECLINED').sort((a, b) => a.at - b.at);
    for (const request of ordered) {
        for (const asked of request.items) {
            const existing = byProduct.get(asked.productId);
            if (existing === undefined) {
                byProduct.set(asked.productId, {
                    productId: asked.productId,
                    productName: asked.productName,
                    quantity: asked.quantity,
                    unitPrice: asked.shownUnitPrice,
                    listPriceSnapshot: asked.shownListPrice,
                    note: asked.note,
                    source: 'REQUEST',
                    askedQuantity: null,
                    askedNote: null
                });
                continue;
            }
            if (existing.source === 'REQUEST') {
                // The buyer owns their own line: a later tick corrects their earlier one.
                byProduct.set(asked.productId, {
                    ...existing,
                    quantity: asked.quantity,
                    note: asked.note ?? existing.note
                });
                continue;
            }
            // A MEETING line keeps its negotiated quantity, its negotiated price AND
            // the note staff wrote. What the buyer said is recorded ALONGSIDE it, in
            // its own two fields, so neither voice overwrites the other.
            byProduct.set(asked.productId, {
                ...existing,
                askedQuantity: asked.quantity === existing.quantity ? null : asked.quantity,
                askedNote: asked.note ?? existing.askedNote
            });
        }
    }
    return [...byProduct.values()];
}
//# sourceMappingURL=shortlist.js.map